/**
 * Daily Activity Entries — Portal Invites and Portal Completions are entered by
 * the team (+ / − then Save); Application Lodgements and Approvals come live
 * from file data. Writes to /api/gameplan/activity/log (shared company-wide)
 * and shows Week · POD and Month · POD progress against goal.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Send, UserCheck, FileCheck2, CheckCircle2, Minus, Plus, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { backendUrl } from './gameplanUtils';
import { Roll } from './jarvis/Ring';

export const ENTRY_STAGES = [
  { key: 'portals_sent', label: 'Portal invites', icon: Send },
  { key: 'portals', label: 'Portal completions', icon: UserCheck },
  { key: 'lodgements', label: 'Application lodgements', icon: FileCheck2, auto: true },
  { key: 'approvals', label: 'Application approvals', icon: CheckCircle2, auto: true },
];
const MANUAL_STAGES = ENTRY_STAGES.filter((s) => !s.auto);

// Same calendar day the board stamps on deals (Australia/Sydney).
const localToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

const Progress = ({ label, actual, target, testId }) => {
  const pct = target > 0 ? Math.round((actual / target) * 100) : 0;
  const tone = target <= 0 ? '' : pct >= 100 ? 'ok' : pct >= 60 ? 'mid' : 'bad';
  return (
    <div data-testid={testId}>
      <div className="gpj-dprow"><span>{label}</span><span><b>{Math.round(actual)}</b> / {target}<span className={`pc ${tone}`}>{target > 0 ? `${pct}%` : '—'}</span></span></div>
      <div className={`gpj-dbar ${tone}`}><i style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} /></div>
    </div>
  );
};

const EntryCard = ({ stage, value, autoValue, onChange, scoreboard }) => {
  const Icon = stage.icon;
  const slug = stage.key.replace(/_/g, '-');
  return (
    <div className="gpj-dc" data-testid={`activity-entry-card-${slug}`}>
      <div className="gpj-dct">
        <Icon strokeWidth={2.1} />
        <span>{stage.label}</span>
        {stage.auto
          ? <span className="gpj-mode live" data-testid={`activity-entry-live-${slug}`}><i /> Live</span>
          : <span className="gpj-mode">Manual</span>}
      </div>
      {stage.auto ? (
        <div className="gpj-dbig" data-testid={`activity-entry-value-${slug}`}><Roll value={autoValue ?? 0} /><small>today</small></div>
      ) : (
        <div className="gpj-stepper">
          <button type="button" onClick={() => onChange(Math.max(0, value - 1))} data-testid={`activity-entry-dec-${slug}`} aria-label={`Decrease ${stage.label}`}><Minus /></button>
          <input type="number" min={0} max={999} value={value} onChange={(e) => onChange(Math.max(0, Math.min(999, Number(e.target.value) || 0)))} data-testid={`activity-entry-input-${slug}`} />
          <button type="button" onClick={() => onChange(Math.min(999, value + 1))} data-testid={`activity-entry-inc-${slug}`} aria-label={`Increase ${stage.label}`}><Plus /></button>
        </div>
      )}
      <div className="gpj-dprog">
        <Progress label="Week · POD" actual={scoreboard?.actual_week ?? 0} target={scoreboard?.target_weekly ?? 0} testId={`activity-progress-week-${slug}`} />
        <Progress label="Month · POD" actual={scoreboard?.actual_month ?? 0} target={scoreboard?.target_monthly ?? 0} testId={`activity-progress-month-${slug}`} />
      </div>
    </div>
  );
};

export const ActivityEntryPanel = ({ authToken, month, onSaved, refreshSignal }) => {
  const [date, setDate] = useState(localToday);
  const [values, setValues] = useState({});
  const [auto, setAuto] = useState({});
  const [scoreboard, setScoreboard] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadDay = useCallback(async () => {
    if (!authToken || !date) return;
    setLoading(true);
    try {
      const r = await fetch(`${backendUrl}/api/gameplan/activity/today?date=${date}`, { headers: { Authorization: `Bearer ${authToken}` } });
      if (!r.ok) throw new Error('failed');
      const d = await r.json();
      const next = {};
      MANUAL_STAGES.forEach((s) => { next[s.key] = d.manual?.[s.key]?.count ?? 0; });
      setValues(next);
      setAuto(d.auto || {});
    } catch (_) {
      toast.error('Could not load activity for that day');
    } finally { setLoading(false); }
  }, [authToken, date]);

  const loadScoreboard = useCallback(async () => {
    const m = date ? date.slice(0, 7) : month;
    if (!authToken || !m) return;
    try {
      const r = await fetch(`${backendUrl}/api/gameplan/activity/scoreboard/${m}?scope=company`, { headers: { Authorization: `Bearer ${authToken}` } });
      if (!r.ok) return;
      const d = await r.json();
      const byKey = {};
      (d.stages || []).forEach((s) => { byKey[s.key] = s; });
      setScoreboard(byKey);
    } catch (_) { /* progress is optional */ }
  }, [authToken, date, month]);

  useEffect(() => { loadDay(); }, [loadDay, refreshSignal]);
  useEffect(() => { loadScoreboard(); }, [loadScoreboard, refreshSignal]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${backendUrl}/api/gameplan/activity/log`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, entries: MANUAL_STAGES.map((s) => ({ stage: s.key, count: values[s.key] ?? 0 })) }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`Activity saved for ${date}`);
      await loadScoreboard();
      onSaved?.();
    } catch (_) {
      toast.error('Could not save activity — please try again');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="activity-entry-panel">
      <div className="gpj-dhead">
        <div><h2>Daily Activity Entries</h2><p>Portal activity is entered by the team. Lodgements and approvals come from file data.</p></div>
        <div className="gpj-dateinp">
          <input type="date" value={date} max={localToday()} onChange={(e) => setDate(e.target.value)} data-testid="activity-entry-date" aria-label="Entry date" />
          <button type="button" className="gpj-btn primary" onClick={save} disabled={saving || loading} data-testid="activity-entry-save">
            {saving ? <Loader2 className="animate-spin" /> : <Save />} Save
          </button>
        </div>
      </div>
      <div className="gpj-dgrid" style={loading ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
        {ENTRY_STAGES.map((s) => (
          <EntryCard key={s.key} stage={s} value={values[s.key] ?? 0} autoValue={auto[s.key]} scoreboard={scoreboard[s.key]}
            onChange={(v) => setValues((prev) => ({ ...prev, [s.key]: v }))} />
        ))}
      </div>
    </div>
  );
};

export default ActivityEntryPanel;
